import {
  AboutFormData,
  OppvarmingBioenergiFormData,
  OppvarmingElektriskFormData,
  OppvarmingGassFormData,
  OppvarmingOljeFormData
} from '../types';
import {
  KjelKaminVannbarenVarme,
  OppvarmingSol,
  OppvarmingVarmepumpe
} from '../../../../../../types/registration/userInput';
import { OppvarmingBruk } from '../../../../utils/registerEnums';

export const validateKjel = (kjel?: KjelKaminVannbarenVarme) => {
  // We do not require kjel validation if use of heat is "Tappevann"
  if (!kjel || kjel.bruk === OppvarmingBruk.Tappevann) {
    return true;
  }
  return !(!kjel.gulvvarme && !kjel.radiator);
};

export const hasSelectedHeatingSource = (formData?: AboutFormData) => {
  const oppvarmingSelection = formData?.oppvarmingSelection;

  // If heating sources is selected, but used as "Tappevann", we do not consider it selected
  const bioenergiHeatingSource = formData?.oppvarming?.oppvarmingBioenergi;
  const gassHeatingSource = formData?.oppvarming?.oppvarmingGass;
  const elektriskHeatingSource = formData?.oppvarming?.oppvarmingElektrisk;
  const oljeHeatingSource = formData?.oppvarming?.oppvarmingOlje;
  const solHeatingSource = formData?.oppvarming?.oppvarmingSol;
  const varmepumpeHeatingSource = formData?.oppvarming?.oppvarmingVarmepumpe;

  return (
    (oppvarmingSelection?.oppvarmingBioenergi && hasBioenergiHeatingSource(bioenergiHeatingSource)) ||
    (oppvarmingSelection?.oppvarmingElektrisk && hasElektriskHeatingSource(elektriskHeatingSource)) ||
    oppvarmingSelection?.oppvarmingFjernvarme ||
    (oppvarmingSelection?.oppvarmingGass && hasGassHeatingSource(gassHeatingSource)) ||
    (oppvarmingSelection?.oppvarmingSol && hasSolHeatingSource(solHeatingSource)) ||
    (oppvarmingSelection?.oppvarmingOlje && hasOljeHeatingSource(oljeHeatingSource)) ||
    (oppvarmingSelection?.oppvarmingVarmepumpe && hasVarmepumpeHeatingSource(varmepumpeHeatingSource)) ||
    oppvarmingSelection?.oppvarmingVed
  );
};

// Check that bioenergi has a heating source that is not only used for "Tappevann"
const hasBioenergiHeatingSource = (bioenergi?: OppvarmingBioenergiFormData) => {
  if (!bioenergi) {
    return false;
  }

  return (
    bioenergi.kamin?.selected ||
    (bioenergi.kjel?.selected && bioenergi.kjel?.value?.bruk !== OppvarmingBruk.Tappevann) ||
    (bioenergi.kaminMedVannbarenVarme?.selected &&
      bioenergi.kaminMedVannbarenVarme?.value?.bruk !== OppvarmingBruk.Tappevann)
  );
};

// Check that gass has a heating source that is not used for "Tappevann"
const hasGassHeatingSource = (gass?: OppvarmingGassFormData) => {
  if (!gass) {
    return false;
  }

  return gass.kamin?.selected || (gass.kjel?.selected && gass.kjel?.value?.bruk !== OppvarmingBruk.Tappevann);
};

// Check that olje has a heating source that is not used for "Tappevann"
const hasOljeHeatingSource = (olje?: OppvarmingOljeFormData) => {
  if (!olje) {
    return false;
  }

  return olje.kamin?.selected || (olje.kjel?.selected && olje.kjel?.value?.bruk !== OppvarmingBruk.Tappevann);
};

// Check that varmepumpe has a heating source that is not used for "Tappevann"
const hasVarmepumpeHeatingSource = (varmepumpe?: OppvarmingVarmepumpe) => {
  if (!varmepumpe) {
    return false;
  }

  return (
    (!!varmepumpe.ventilasjon && varmepumpe.ventilasjon.bruk !== OppvarmingBruk.Tappevann) ||
    (!!varmepumpe.vannbarenVarme && varmepumpe.vannbarenVarme.bruk !== OppvarmingBruk.Tappevann) ||
    !!varmepumpe.punktoppvarming
  );
};

// Check that sol has a heating source that is not used for "Tappevann"
const hasSolHeatingSource = (sol?: OppvarmingSol) => {
  if (!sol) {
    return false;
  }

  return !!sol.solfangerMedVannbarenVarme && sol.solfangerMedVannbarenVarme.bruk !== OppvarmingBruk.Tappevann;
};

// Check that elektrisk has a heating source that is not used for "Tappevann"
const hasElektriskHeatingSource = (elektrisk?: OppvarmingElektriskFormData) => {
  if (!elektrisk) {
    return false;
  }

  return (
    !!elektrisk.gulvvarme?.selected ||
    (!!elektrisk.kjel?.selected && elektrisk.kjel.value?.bruk !== OppvarmingBruk.Tappevann) ||
    !!elektrisk.luftvarme?.selected ||
    !!elektrisk.panelovn?.selected ||
    !!elektrisk.takvarme?.selected
  );
};
